import {PayloadAction, createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {addResultCases} from 'utils/helpers';
import {ListResult, SingleResult} from 'utils/types';
import {
  URLParamsSerializer,
  WithOrderingURLParams,
  WithPaginationURLParams,
  withOrderingURLParams,
  withPaginationURLParams,
} from 'utils/urlParamHelpers';

export interface CampaignMatch {
  path_id: string;
  campaign_name: string;
  start_date: string | null;
  end_date: string | null;
}

export type InvoiceStatus = 'draw' | 'paid' | 'overdue' | 'open';

/** Which date decides the month an invoice belongs to. Mirrors `MonthlySummaryBasis` server-side. */
export type MonthlySummaryBasis = 'campaign_start' | 'campaign_end' | 'txn_date' | 'due_date';

export interface Invoice {
  id: number;
  external_id: string;
  doc_number: string;
  txn_date: string | null;
  due_date: string | null;
  /** Serialized as a string, not a number - see COERCE_DECIMAL_TO_STRING on the backend. */
  total_amount: string | null;
  balance: string | null;
  status: InvoiceStatus;
  project_code: string;
  path_ids: string[];
  customer_name: string;
  /** Empty when the source could not supply a hosted invoice / pay link. */
  hosted_invoice_url: string;
  /** Staff-only: absent for client users, who only ever see sent invoices. */
  sent?: boolean;
  campaign_name: string | null;
  campaign_start_date: string | null;
  campaign_end_date: string | null;
  campaigns: CampaignMatch[];
  synced_at: string;
}

/**
 * Serves both sets of tiles. The Invoices tab reads `open_*`/`overdue_*` - where the client
 * stands right now - while Monthly Summary reads the `*_amount` trio, which partitions one
 * month's billing. `outstanding_amount` is every row's balance, whereas `open_balance` counts
 * only open and overdue rows, so the two are not interchangeable.
 *
 * Unsent invoices (staff only) count towards `invoice_count`, matching the table, but are
 * excluded from every other total and reported as `unsent_*`, which only staff receive.
 */
export interface InvoiceSummary {
  invoice_count: number;
  open_balance: string;
  open_count: number;
  overdue_balance: string;
  overdue_count: number;
  billed_amount: string;
  paid_amount: string;
  outstanding_amount: string;
  unsent_balance?: string;
  unsent_count?: number;
}

/** The subset of BusinessDetails a caller may write. */
export type BusinessDetailsInput = Partial<
  Pick<
    BusinessDetails,
    | 'legal_name'
    | 'website'
    | 'industry'
    | 'employees'
    | 'address'
    | 'city'
    | 'state'
    | 'zip_code'
    | 'ap_email'
    | 'po_required_on_invoices'
  >
>;

export interface BusinessDetails {
  client_id: number;
  hubspot_id: string;
  /** False when no HubSpot company is linked, or HubSpot could not be reached. */
  available: boolean;
  legal_name: string | null;
  website: string | null;
  industry: string | null;
  employees: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  ap_email: string | null;
  po_required_on_invoices: boolean;
}

/**
 * `client` is always sent: the backend requires it for a staff caller, and it is harmless for
 * a client user (whose queryset is already scoped to their own client regardless).
 * The free-text parameter is `q`, not the usual `search`, because it spans the invoice's own
 * columns and the campaigns joined by Path ID.
 */
export type WithInvoiceFilterURLParams<T extends object = {}> = T & {
  client: number;
  status?: string;
  q?: string;
  /** `YYYY-MM`. Only meaningful together with `basis`, which defaults to `txn_date`. */
  month?: string;
  basis?: MonthlySummaryBasis;
};
const withInvoiceFilterURLParams =
  <T extends object = {}>(
    next: URLParamsSerializer<WithInvoiceFilterURLParams<T>> = () => {},
  ): URLParamsSerializer<WithInvoiceFilterURLParams<T>> =>
  (params, payload) => {
    params.set('client', `${payload.client}`);
    if (payload.status) params.set('status', payload.status);
    if (payload.q) params.set('q', payload.q);
    if (payload.month) params.set('month', payload.month);
    if (payload.basis) params.set('basis', payload.basis);
    next(params, payload);
  };

export type InvoiceListParams = WithInvoiceFilterURLParams<WithOrderingURLParams<WithPaginationURLParams>>;
const serializeInvoiceListParams = withInvoiceFilterURLParams(withOrderingURLParams(withPaginationURLParams()));

/** The tiles and the export cover the whole filtered period, so neither pages nor sorts. */
export type InvoiceSummaryParams = WithInvoiceFilterURLParams;
const serializeInvoiceSummaryParams = withInvoiceFilterURLParams();

/**
 * The Monthly Summary tab gets its own two slots rather than sharing the Invoices tab's.
 * `<Tabs>` uses `mountOnEnter` without `unmountOnExit`, so once a tab has been visited it stays
 * mounted and keeps refetching on every client change - sharing one slot would have the two
 * tabs overwrite each other's rows and tiles.
 */
export interface IState {
  invoices: ListResult<Invoice>;
  summary: SingleResult<InvoiceSummary>;
  monthlyInvoices: ListResult<Invoice>;
  monthlySummary: SingleResult<InvoiceSummary>;
  businessDetails: SingleResult<BusinessDetails>;
}

const initialState: IState = {
  invoices: {as: 'idle'},
  summary: {as: 'idle'},
  monthlyInvoices: {as: 'idle'},
  monthlySummary: {as: 'idle'},
  businessDetails: {as: 'idle'},
};

export const clientPortalEndpoints = {
  getInvoices: createAsyncThunk('clientPortal/getInvoices', async (payload: InvoiceListParams) => {
    const params = new URLSearchParams();
    serializeInvoiceListParams(params, payload);
    const response = await ApiService.get(`/client_portal/invoices/?${params.toString()}`);
    return response.data;
  }),
  getInvoiceSummary: createAsyncThunk(
    'clientPortal/getInvoiceSummary',
    async (clientId: number): Promise<SingleResult<InvoiceSummary>> => {
      const response = await ApiService.get<InvoiceSummary>(`/client_portal/invoices/summary/?client=${clientId}`);
      return {as: 'success', value: response.data};
    },
  ),
  getMonthlyInvoices: createAsyncThunk('clientPortal/getMonthlyInvoices', async (payload: InvoiceListParams) => {
    const params = new URLSearchParams();
    serializeInvoiceListParams(params, payload);
    const response = await ApiService.get(`/client_portal/invoices/?${params.toString()}`);
    return response.data;
  }),
  getMonthlySummary: createAsyncThunk(
    'clientPortal/getMonthlySummary',
    async (payload: InvoiceSummaryParams): Promise<SingleResult<InvoiceSummary>> => {
      const params = new URLSearchParams();
      serializeInvoiceSummaryParams(params, payload);
      const response = await ApiService.get<InvoiceSummary>(`/client_portal/invoices/summary/?${params.toString()}`);
      return {as: 'success', value: response.data};
    },
  ),
  /**
   * Resolves to the spreadsheet itself, for the caller to hand to `downloadFile`. Takes the same
   * filters as the list, minus pagination - the export is the whole month, not the page on screen.
   */
  exportInvoices: createAsyncThunk('clientPortal/exportInvoices', async (payload: InvoiceSummaryParams) => {
    const params = new URLSearchParams();
    serializeInvoiceSummaryParams(params, payload);
    const response = await ApiService.get<Blob>(`/client_portal/invoices/export/?${params.toString()}`, {
      responseType: 'blob',
      timeout: 120_000,
    });
    return response.data;
  }),
  getBusinessDetails: createAsyncThunk(
    'clientPortal/getBusinessDetails',
    async (clientId: number): Promise<SingleResult<BusinessDetails>> => {
      const response = await ApiService.get<BusinessDetails>(`/client_portal/business_details/${clientId}/`);
      return {as: 'success', value: response.data};
    },
  ),
  updateBusinessDetails: createAsyncThunk(
    'clientPortal/updateBusinessDetails',
    async (
      {clientId, changes}: {clientId: number; changes: BusinessDetailsInput},
      {rejectWithValue},
    ): Promise<SingleResult<BusinessDetails>> => {
      try {
        const response = await ApiService.patch<BusinessDetails>(
          `/client_portal/business_details/${clientId}/`,
          changes,
        );
        return {as: 'success', value: response.data};
      } catch (error: any) {
        // Surface the field errors so the form can show them rather than a bare failure.
        return rejectWithValue(error.response?.data) as never;
      }
    },
  ),
};

export const clientPortalSlice = createSlice({
  name: 'clientPortal',
  initialState,
  reducers: {
    /** Adopt the record a save returned, without spending a second request re-reading it. */
    businessDetailsLoaded(state, action: PayloadAction<BusinessDetails>) {
      state.businessDetails = {as: 'success', value: action.payload};
    },
  },
  extraReducers: builder => {
    // addResultCases carries the requestId through, so a slow earlier response cannot overwrite
    // a newer one - which matters here because the table refetches on every filter, sort and
    // page change. It also keeps the previous result available to getResult() while the next
    // one is in flight, so the table does not blank out between pages.
    addResultCases(builder, clientPortalEndpoints.getInvoices, 'invoices');
    addResultCases(builder, clientPortalEndpoints.getInvoiceSummary, 'summary');
    addResultCases(builder, clientPortalEndpoints.getMonthlyInvoices, 'monthlyInvoices');
    addResultCases(builder, clientPortalEndpoints.getMonthlySummary, 'monthlySummary');
    addResultCases(builder, clientPortalEndpoints.getBusinessDetails, 'businessDetails');
    // exportInvoices is likewise absent: it has no state slot, because a downloaded file is not
    // page state. useMutation tracks the download's own progress at the call site.
    // updateBusinessDetails deliberately does NOT go through addResultCases: a rejected save
    // would land in the same slot as the fetch and the tab would swap the whole form for an
    // error alert, losing what was typed. useMutation tracks the save's own state, and the
    // record it returns is adopted through businessDetailsLoaded.
  },
});

export const clientPortalActions = clientPortalSlice.actions;

export type ClientPortalSlice = {
  [clientPortalSlice.name]: ReturnType<(typeof clientPortalSlice)['reducer']>;
};
